import type { Transaction } from '@/lib/api'

export type PayerSummary = {
  paidBy: string
  name: string | null
  avatarUrl: string | null
  paid: number
  share: number
  balance: number
}

export type SettlementTransfer = {
  from: string
  to: string
  fromName: string | null
  toName: string | null
  amount: number
}

export type SettlementResult = {
  total: number
  payers: PayerSummary[]
  transfers: SettlementTransfer[]
}

/**
 * 取引一覧から支払者ごとの集計と均等割り精算案を計算する。
 *
 * 各人の負担額 = 合計 / 人数。
 * 負担を超えて払った人 (balance > 0) は受け取り側、不足の人 (balance < 0) は支払い側。
 * 精算は貪欲法で取引数を最小化する: 最大の貸し手と最大の借り手をマッチング。
 */
export function computeSettlement(transactions: Transaction[]): SettlementResult {
  const totalsByPayer = new Map<string, PayerSummary>()
  let total = 0

  for (const tx of transactions) {
    total += tx.total_amount
    const existing = totalsByPayer.get(tx.paid_by)
    if (existing) {
      existing.paid += tx.total_amount
    } else {
      totalsByPayer.set(tx.paid_by, {
        paidBy: tx.paid_by,
        name: tx.paid_by_name,
        avatarUrl: tx.paid_by_avatar_url ?? null,
        paid: tx.total_amount,
        share: 0,
        balance: 0,
      })
    }
  }

  const payers = Array.from(totalsByPayer.values())
  const n = payers.length
  if (n === 0) {
    return { total: 0, payers: [], transfers: [] }
  }

  const share = total / n
  for (const p of payers) {
    p.share = share
    p.balance = p.paid - share
  }

  payers.sort((a, b) => b.paid - a.paid)

  const transfers = buildTransfers(payers)

  return { total, payers, transfers }
}

function buildTransfers(payers: PayerSummary[]): SettlementTransfer[] {
  const creditors = payers
    .filter(p => p.balance > 0.5)
    .map(p => ({ ...p, remaining: p.balance }))
    .sort((a, b) => b.remaining - a.remaining)
  const debtors = payers
    .filter(p => p.balance < -0.5)
    .map(p => ({ ...p, remaining: -p.balance }))
    .sort((a, b) => b.remaining - a.remaining)

  const transfers: SettlementTransfer[] = []
  let i = 0
  let j = 0
  while (i < debtors.length && j < creditors.length) {
    const debtor = debtors[i]
    const creditor = creditors[j]
    const amount = Math.round(Math.min(debtor.remaining, creditor.remaining))
    if (amount > 0) {
      transfers.push({
        from: debtor.paidBy,
        to: creditor.paidBy,
        fromName: debtor.name,
        toName: creditor.name,
        amount,
      })
    }
    debtor.remaining -= amount
    creditor.remaining -= amount
    if (debtor.remaining < 0.5) i++
    if (creditor.remaining < 0.5) j++
  }

  return transfers
}
